import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { BrandMark } from './BrandMark';

interface ValidateResponse {
  email: string;
  valid: boolean;
  expiresAt?: string;
}

const fieldClass =
  'w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm shadow-sm transition-shadow focus:outline-none focus:ring-2 focus:ring-teal-600/30 focus:border-teal-600 disabled:opacity-60';

const RegisterFromInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [status, setStatus] = useState<'loading' | 'invalid' | 'valid'>('loading');
  const [email, setEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const [userDisplayName, setUserDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<ValidateResponse>(`/auth/invitation/${token}`);
        if (cancelled) return;
        if (data.valid) {
          setEmail(data.email);
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      } catch {
        if (!cancelled) setStatus('invalid');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!userPassword.trim() || userPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!userFirstName.trim()) {
      setError('First name is required.');
      return;
    }
    if (!userLastName.trim()) {
      setError('Last name is required.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/register-with-invitation', {
        token,
        userPassword: userPassword.trim(),
        userFirstName: userFirstName.trim(),
        userLastName: userLastName.trim(),
        userDisplayName: userDisplayName.trim() || undefined,
      });
      navigate('/login?registered=1');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string }; message?: string } }).response?.data
              ?.message
          : undefined;
      setError(message || 'Registration failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center app-mesh-bg">
        <div className="flex items-center gap-3 text-slate-600 animate-fade-in">
          <BrandMark size="sm" />
          <p className="text-sm font-medium">Checking invitation…</p>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center app-mesh-bg px-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200/80 shadow-soft p-8 text-center animate-slide-up">
          <BrandMark size="md" className="mx-auto mb-4" />
          <h2 className="font-display text-xl font-bold text-slate-900 mb-2">
            Invalid or expired invitation
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            This link may have expired or already been used. Please request a new invitation.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors"
          >
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex app-mesh-bg">
      <aside className="hidden lg:flex lg:w-[42%] relative overflow-hidden auth-panel-glow text-white flex-col justify-between p-12">
        <div className="relative z-10 flex items-center gap-3">
          <BrandMark size="md" inverted />
          <div>
            <p className="font-display text-xl font-bold tracking-tight">UMS</p>
            <p className="text-sm text-teal-100/80">Unibyte Management System</p>
          </div>
        </div>
        <div className="relative z-10 max-w-sm animate-slide-up">
          <h1 className="font-display text-3xl font-bold leading-tight tracking-tight">
            You&apos;re invited.
          </h1>
          <p className="mt-4 text-sm text-slate-200/85 leading-relaxed">
            Set up your account to join your team on Unibyte.
          </p>
        </div>
        <p className="relative z-10 text-xs text-teal-100/50">
          © {new Date().getFullYear()} Unibyte
        </p>
      </aside>

      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-[420px] animate-slide-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <BrandMark size="md" />
            <p className="font-display text-lg font-bold text-slate-900">UMS</p>
          </div>

          <div className="mb-6">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">
              Create your account
            </h2>
            <p className="mt-1.5 text-sm text-slate-500">
              Complete the form below. Your email comes from the invitation.
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-xl bg-rose-50 border border-rose-200/80 px-4 py-3 text-sm text-rose-800">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                readOnly
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Password (min 6 characters)
              </label>
              <input
                type="password"
                value={userPassword}
                onChange={(e) => setUserPassword(e.target.value)}
                className={fieldClass}
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">First name</label>
                <input
                  type="text"
                  value={userFirstName}
                  onChange={(e) => setUserFirstName(e.target.value)}
                  className={fieldClass}
                  placeholder="First name"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Last name</label>
                <input
                  type="text"
                  value={userLastName}
                  onChange={(e) => setUserLastName(e.target.value)}
                  className={fieldClass}
                  placeholder="Last name"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Display name (optional)
              </label>
              <input
                type="text"
                value={userDisplayName}
                onChange={(e) => setUserDisplayName(e.target.value)}
                className={fieldClass}
                placeholder="Display name"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-teal-600 disabled:opacity-50 transition-colors shadow-soft"
            >
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-teal-700 hover:text-teal-800">
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default RegisterFromInvitation;
